import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import client from "../lib/api";
import { Panel, DemoTag, AiText, Spinner, StatusBadge } from "../components/ui-kit";
import { Sparkles, Newspaper, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const impactColor = (i) => (i === "High" ? "text-bear border-[color:var(--bear)]" : i === "Medium" ? "text-gold border-gold" : "text-slate-400 border-slate-600");

export function News({ embed }) {
  const [items, setItems] = useState(null);
  const [events, setEvents] = useState([]);
  useEffect(() => {
    client.get("/news/items").then(({ data }) => setItems(data));
    client.get("/news/events").then(({ data }) => setEvents(data));
  }, []);
  const [scenario, setScenario] = useState({ id: null, text: "", loading: false });

  const genScenario = async (ev) => {
    setScenario({ id: ev.id, text: "", loading: true });
    try { const { data } = await client.post(`/news/scenario/${ev.id}`); setScenario({ id: ev.id, text: data.scenario, loading: false }); }
    catch { toast.error("Failed"); setScenario({ id: null, text: "", loading: false }); }
  };

  const body = (
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2 space-y-4">
          <Panel title="High-Impact Events (Gold & BTC)" right={<DemoTag />}>
            {!events ? <Spinner /> : events.filter((e) => e.impact !== "Low").slice(0, 8).map((ev) => (
              <div key={ev.id} className="px-4 py-3 border-b border-[color:var(--border-subtle)] last:border-0">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm text-white truncate">{ev.title}</div>
                    <div className="text-xs text-slate-500 font-mono mt-0.5">Fcst {ev.forecast} · Prev {ev.previous} · Affects {ev.affects}</div>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 text-[11px] font-mono uppercase border ${impactColor(ev.impact)}`}>{ev.impact}</span>
                </div>
                <button onClick={() => genScenario(ev)} className="mt-2 text-xs text-gold flex items-center gap-1 hover:underline"><Sparkles size={12} />Pre-news scenario</button>
                {scenario.id === ev.id && <div className="mt-2 p-3 bg-[color:var(--app-bg)] border border-[color:var(--border-subtle)]">{scenario.loading ? <Spinner /> : <AiText text={scenario.text} />}</div>}
              </div>
            ))}
          </Panel>
        </div>
        <div>
          <Panel title="Market Wire" right={<Newspaper size={15} className="text-slate-500" />}>
            {!items ? <Spinner /> : items.map((n) => (
              <div key={n.id} className="px-4 py-3 border-b border-[color:var(--border-subtle)] last:border-0">
                <div className="text-sm text-slate-200">{n.title}</div>
                <div className="text-xs text-slate-500 mt-1 flex items-center gap-2"><span className={n.affects === "Gold" ? "text-gold" : "text-btc"}>{n.affects}</span> · {n.source}</div>
              </div>
            ))}
          </Panel>
        </div>
      </div>
  );
  return embed ? body : <Layout title="News & Impact">{body}</Layout>;
}

export function Calendar() {
  const [events, setEvents] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const load = () => client.get("/news/events").then(({ data }) => setEvents(data));
  useEffect(() => { load(); }, []);
  const refresh = async () => {
    setRefreshing(true);
    try { const { data } = await client.get("/economic/refresh"); data.ok ? toast.success(`Live calendar refreshed · ${data.count} events`) : toast.error(data.error || "Failed"); load(); }
    catch { toast.error("Refresh failed"); } finally { setRefreshing(false); }
  };
  const live = (events || []).some((e) => e.source === "biquote");
  return (
    <Layout title="Economic Calendar">
      <Panel title="Upcoming Economic Events" right={
        <div className="flex items-center gap-2">
          <span className={`text-[11px] px-2 py-0.5 border font-mono ${live ? "border-bull text-bull" : "border-slate-600 text-slate-400"}`}>{live ? "LIVE · BiQuote" : "DEMO"}</span>
          <button data-testid="econ-refresh" onClick={refresh} disabled={refreshing} className="text-xs px-2.5 py-1 border border-gold text-gold hover:bg-[rgba(212,175,55,0.1)] transition-colors duration-200 disabled:opacity-60">{refreshing ? "Refreshing…" : "Refresh live"}</button>
        </div>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[color:var(--border-subtle)]">
              <th className="px-4 py-2.5">Time</th><th className="px-4 py-2.5">Event</th><th className="px-4 py-2.5">Impact</th><th className="px-4 py-2.5">Affects</th><th className="px-4 py-2.5 text-right font-mono">Fcst</th><th className="px-4 py-2.5 text-right font-mono">Prev</th><th className="px-4 py-2.5 text-right font-mono">Actual</th>
            </tr></thead>
            <tbody>
              {!events ? <tr><td colSpan="7"><Spinner /></td></tr> : events.map((ev) => (
                <tr key={ev.id} className="border-b border-[color:var(--border-subtle)] hover:bg-white/5 transition-colors duration-150">
                  <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{new Date(ev.time).toLocaleDateString()} {new Date(ev.time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</td>
                  <td className="px-4 py-2.5 text-slate-200">{ev.title}</td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 text-[11px] font-mono uppercase border ${impactColor(ev.impact)}`}>{ev.impact}</span></td>
                  <td className="px-4 py-2.5"><span className={ev.affects === "Bitcoin" ? "text-btc" : ev.affects === "Gold" ? "text-gold" : "text-slate-300"}>{ev.affects}</span></td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-300">{ev.forecast ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-500">{ev.previous ?? "—"}</td>
                  <td className="px-4 py-2.5 text-right font-mono text-slate-300">{ev.actual ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </Layout>
  );
}
